import React, { createContext, useContext, useEffect, useState } from 'react';
import { useSocket } from './SocketContext';
import { useAuthStore } from '../store/authStore';
import { notificationService, AppNotification } from '../services/notificationService';
import { socketService } from '../services/socketService';
import { toast } from '../components/ui/Toaster';

interface NotificationContextType {
  notifications: AppNotification[];
  unreadCount: number;
  markAsRead: (id: number) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (id: number) => Promise<void>;
  refreshNotifications: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: React.ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const { isConnected } = useSocket();
  const { isAuthenticated } = useAuthStore();

  // Load notifications on mount (only if authenticated)
  useEffect(() => {
    if (isAuthenticated) {
      loadNotifications();
      loadUnreadCount();
    } else {
      setNotifications([]);
      setUnreadCount(0);
    }
  }, [isAuthenticated]);

  // Additional loading when socket connects for real-time updates
  useEffect(() => {
    if (isConnected) {
      loadNotifications();
      loadUnreadCount();
    }
  }, [isConnected]);

  // Socket event listeners for real-time notifications
  useEffect(() => {
    if (!isConnected) return;

    // Listen for new notifications
    const handleNewNotification = (notification: AppNotification) => {
      setNotifications(prev => [notification, ...prev]);
      setUnreadCount(prev => prev + 1);
      
      // Show toast notification
      toast.info(notification.title, notification.message);
    };

    // Listen for count updates
    const handleCountUpdate = (count: number) => {
      setUnreadCount(count);
    };

    socketService.onNewNotification(handleNewNotification);
    socketService.onNotificationCountUpdate(handleCountUpdate);

    return () => {
      socketService.offNewNotification(handleNewNotification);
      socketService.offNotificationCountUpdate(handleCountUpdate);
    };
  }, [isConnected]);

  const loadNotifications = async () => {
    try {
      const response = await notificationService.getNotifications();
      setNotifications(response.notifications || []);
    } catch (error) {
      console.error('Failed to load notifications:', error);
      setNotifications([]); // Set empty array on error to prevent crashes
    }
  };

  const loadUnreadCount = async () => {
    try {
      const response = await notificationService.getUnreadCount();
      setUnreadCount(response.count || 0);
    } catch (error) {
      console.error('Failed to load unread count:', error);
      setUnreadCount(0); // Set 0 on error to prevent crashes
    }
  };

  const markAsRead = async (id: number) => {
    try {
      await notificationService.markAsRead(id);
      setNotifications(prev =>
        prev.map(n => n.id === id ? { ...n, isRead: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const deleteNotification = async (id: number) => {
    try {
      await notificationService.deleteNotification(id);
      setNotifications(prev => prev.filter(n => n.id !== id));
      // Count will be updated via socket event
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  return (
    <NotificationContext.Provider value={{
      notifications,
      unreadCount,
      markAsRead,
      markAllAsRead,
      deleteNotification,
      refreshNotifications: loadNotifications,
    }}>
      {children}
    </NotificationContext.Provider>
  );
};

// Legacy exports for backward compatibility (these are not used anymore but keep for existing imports)
export const notificationHelpers = {
  eventCreated: () => ({ type: 'event_created', title: '', message: '' }),
  eventApproved: () => ({ type: 'event_approved', title: '', message: '' }),
  eventRejected: () => ({ type: 'event_rejected', title: '', message: '' }),
  groupJoinAccepted: () => ({ type: 'group_join_accepted', title: '', message: '' }),
  groupJoinRejected: () => ({ type: 'group_join_rejected', title: '', message: '' }),
  groupRequestReceived: () => ({ type: 'group_request_received', title: '', message: '' }),
};